import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Check, X, Trash2, Eye, RefreshCw } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from './ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from './ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from './ui/select';
import { hrLifecycleAPI, employeeAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { confirmToast } from '../lib/confirmToast';

const EVENT_TYPES = [
  'promotion', 'transfer', 'warning', 'suspension', 'work_trip',
  'salary_revision', 'probation_confirmation', 'department_change', 'designation_change',
];
const STATUSES = ['pending', 'approved', 'rejected', 'cancelled'];
const APPROVER_ROLES = ['workspace_admin', 'admin', 'hr_admin'];

const titleize = (s) => String(s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const statusVariant = (s) => ({
  pending: 'secondary', approved: 'default', rejected: 'destructive', cancelled: 'outline',
}[s] || 'secondary');

// Which new_value fields matter per event type (drives the dynamic form).
const FIELDS_BY_TYPE = {
  promotion: [['designation', 'New Designation'], ['grade', 'New Grade']],
  designation_change: [['designation', 'New Designation'], ['grade', 'New Grade']],
  transfer: [['department', 'New Department'], ['sub_department', 'New Sub-Department'], ['location', 'New Location']],
  department_change: [['department', 'New Department'], ['sub_department', 'New Sub-Department']],
  salary_revision: [['ctc_annual', 'New CTC (Annual)'], ['basic_salary', 'New Basic'], ['gross_salary', 'New Gross']],
  suspension: [], probation_confirmation: [], warning: [], work_trip: [['location', 'Destination']],
};

const HRLifecycleManagement = () => {
  const { user } = useAuth();
  const canApprove = APPROVER_ROLES.includes(user?.role);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });
  const [employees, setEmployees] = useState([]);

  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ employee_id: '', event_type: 'promotion', effective_date: '', reason: '', new_value: {} });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (filterType !== 'all') params.event_type = filterType;
      if (filterStatus !== 'all') params.status = filterStatus;
      if (search.trim()) params.search = search.trim();
      const [listRes, sumRes] = await Promise.all([
        hrLifecycleAPI.list(params),
        hrLifecycleAPI.summary().catch(() => ({ data: {} })),
      ]);
      setRows(listRes.data.items || []);
      setTotal(listRes.data.total || 0);
      setSummary(sumRes.data || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load lifecycle events');
    } finally {
      setLoading(false);
    }
  }, [page, filterType, filterStatus, search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    employeeAPI.list().then((r) => setEmployees(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const resetForm = () => { setEditing(null); setForm({ employee_id: '', event_type: 'promotion', effective_date: '', reason: '', new_value: {} }); };

  const openCreate = () => { resetForm(); setShowForm(true); };
  const openEdit = (row) => {
    setEditing(row);
    setForm({
      employee_id: row.employee_id, event_type: row.event_type,
      effective_date: row.effective_date ? row.effective_date.slice(0, 10) : '',
      reason: row.reason || '', new_value: row.new_value || {},
    });
    setShowForm(true);
  };

  const doSubmit = async () => {
    setSaving(true);
    try {
      const payload = {
        employee_id: form.employee_id,
        event_type: form.event_type,
        effective_date: form.effective_date ? new Date(form.effective_date).toISOString() : null,
        reason: form.reason || null,
        new_value: form.new_value || {},
      };
      if (editing) {
        await hrLifecycleAPI.update(editing.id, {
          effective_date: payload.effective_date, reason: payload.reason, new_value: payload.new_value,
        });
        toast.success('Lifecycle event updated');
      } else {
        await hrLifecycleAPI.create(payload);
        toast.success('Lifecycle event created');
      }
      setShowForm(false); resetForm(); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  // Editing an existing record is a critical operation -> confirm before saving.
  const submit = () => {
    if (!form.employee_id) { toast.error('Select an employee'); return; }
    if (editing) {
      confirmToast({
        message: 'Save changes to this lifecycle action?',
        description: `${titleize(form.event_type)} for ${form.employee_id}`,
        confirmLabel: 'Save',
        onConfirm: doSubmit,
      });
    } else {
      doSubmit();
    }
  };

  const act = async (fn, id, okMsg) => {
    try { await fn(id); toast.success(okMsg); load(); if (detail?.id === id) setDetail(null); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
  };

  // Confirmation wrappers for critical operations (approve / reject / delete).
  const confirmApprove = (id, label) => confirmToast({
    message: 'Approve this lifecycle action?',
    description: label ? `This will apply the change to ${label}.` : 'This will apply the change to the employee record.',
    confirmLabel: 'Approve',
    onConfirm: () => act(hrLifecycleAPI.approve, id, 'Approved'),
  });
  const confirmReject = (id) => confirmToast({
    message: 'Reject this lifecycle action?',
    confirmLabel: 'Reject',
    destructive: true,
    onConfirm: () => act((x) => hrLifecycleAPI.reject(x, 'Rejected'), id, 'Rejected'),
  });
  const confirmDelete = (id) => confirmToast({
    message: 'Delete this lifecycle action?',
    description: 'This cannot be undone.',
    confirmLabel: 'Delete',
    destructive: true,
    onConfirm: () => act(hrLifecycleAPI.remove, id, 'Deleted'),
  });

  const pages = Math.max(1, Math.ceil(total / pageSize));
  const formFields = FIELDS_BY_TYPE[form.event_type] || [];

  const stat = (label, value) => (
    <Card><CardContent className="p-4">
      <div className="text-2xl font-bold">{value ?? 0}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </CardContent></Card>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">HR Lifecycle</h2>
          <p className="text-sm text-muted-foreground">Promotions, transfers, warnings, suspensions, salary revisions and more.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load}><RefreshCw className="h-4 w-4 mr-2" />Refresh</Button>
          <Button size="sm" onClick={openCreate}><Plus className="h-4 w-4 mr-2" />New Action</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {stat('Total', summary.total)}
        {stat('Pending', summary.pending)}
        {stat('Approved', summary.approved)}
        {stat('Rejected', summary.rejected)}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap gap-2 items-end">
            <div className="w-48">
              <Label className="text-xs">Type</Label>
              <Select value={filterType} onValueChange={(v) => { setPage(1); setFilterType(v); }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {EVENT_TYPES.map((t) => <SelectItem key={t} value={t}>{titleize(t)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="w-40">
              <Label className="text-xs">Status</Label>
              <Select value={filterStatus} onValueChange={(v) => { setPage(1); setFilterStatus(v); }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{titleize(s)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="w-56">
              <Label className="text-xs">Search reason</Label>
              <Input value={search} onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); load(); } }} placeholder="Search…" />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead><TableHead>Type</TableHead><TableHead>Status</TableHead>
                <TableHead>Effective</TableHead><TableHead>Reason</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Loading…</TableCell></TableRow>}
              {!loading && rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No lifecycle actions found</TableCell></TableRow>}
              {!loading && rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium">{r.employee_id}</TableCell>
                  <TableCell>{titleize(r.event_type)}</TableCell>
                  <TableCell><Badge variant={statusVariant(r.status)}>{titleize(r.status)}</Badge></TableCell>
                  <TableCell>{r.effective_date ? r.effective_date.slice(0, 10) : '—'}</TableCell>
                  <TableCell className="max-w-[220px] truncate">{r.reason || '—'}</TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button variant="ghost" size="icon" title="Details" onClick={() => setDetail(r)}><Eye className="h-4 w-4" /></Button>
                    {r.status === 'pending' && canApprove && (
                      <>
                        <Button variant="ghost" size="icon" title="Approve" onClick={() => confirmApprove(r.id, r.employee_id)}><Check className="h-4 w-4 text-green-600" /></Button>
                        <Button variant="ghost" size="icon" title="Reject" onClick={() => confirmReject(r.id)}><X className="h-4 w-4 text-red-600" /></Button>
                      </>
                    )}
                    {r.status === 'pending' && (
                      <>
                        <Button variant="ghost" size="icon" title="Edit" onClick={() => openEdit(r)}>✎</Button>
                        <Button variant="ghost" size="icon" title="Delete" onClick={() => confirmDelete(r.id)}><Trash2 className="h-4 w-4" /></Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="flex items-center justify-between mt-3">
            <div className="text-xs text-muted-foreground">{total} record(s)</div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
              <span className="text-xs">Page {page} / {pages}</span>
              <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? 'Edit Lifecycle Action' : 'New Lifecycle Action'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Employee</Label>
              <Select value={form.employee_id} onValueChange={(v) => setForm((f) => ({ ...f, employee_id: v }))} disabled={!!editing}>
                <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  {employees.map((e) => <SelectItem key={e.employee_id} value={e.employee_id}>{e.employee_id} — {e.first_name} {e.last_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Action Type</Label>
              <Select value={form.event_type} onValueChange={(v) => setForm((f) => ({ ...f, event_type: v, new_value: {} }))} disabled={!!editing}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{EVENT_TYPES.map((t) => <SelectItem key={t} value={t}>{titleize(t)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Effective Date {form.event_type === 'salary_revision' && <span className="text-xs text-muted-foreground">(payroll applies from this date)</span>}</Label>
              <Input type="date" value={form.effective_date} onChange={(e) => setForm((f) => ({ ...f, effective_date: e.target.value }))} />
            </div>
            {formFields.map(([key, label]) => (
              <div key={key}>
                <Label>{label}</Label>
                <Input value={form.new_value[key] ?? ''} onChange={(e) => setForm((f) => ({ ...f, new_value: { ...f.new_value, [key]: e.target.value } }))} />
              </div>
            ))}
            <div>
              <Label>Reason</Label>
              <Textarea value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving}>{saving ? 'Saving…' : (editing ? 'Update' : 'Create')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Details dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Lifecycle Action Details</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Employee</span><span className="font-medium">{detail.employee_id}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Type</span><span>{titleize(detail.event_type)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Status</span><Badge variant={statusVariant(detail.status)}>{titleize(detail.status)}</Badge></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Effective</span><span>{detail.effective_date ? detail.effective_date.slice(0, 10) : '—'}</span></div>
              <div><span className="text-muted-foreground">Reason</span><div>{detail.reason || '—'}</div></div>
              <div><span className="text-muted-foreground">Requested changes</span><pre className="bg-muted p-2 rounded text-xs overflow-auto">{JSON.stringify(detail.new_value || {}, null, 2)}</pre></div>
              <div><span className="text-muted-foreground">Previous snapshot</span><pre className="bg-muted p-2 rounded text-xs overflow-auto">{JSON.stringify(detail.previous_value || {}, null, 2)}</pre></div>
              {detail.approved_by && <div className="flex justify-between"><span className="text-muted-foreground">Approved by</span><span>{detail.approved_by}</span></div>}
              {detail.rejection_reason && <div className="flex justify-between"><span className="text-muted-foreground">Rejection</span><span>{detail.rejection_reason}</span></div>}
            </div>
          )}
          {detail?.status === 'pending' && canApprove && (
            <DialogFooter>
              <Button variant="outline" onClick={() => confirmReject(detail.id)}>Reject</Button>
              <Button onClick={() => confirmApprove(detail.id, detail.employee_id)}>Approve</Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HRLifecycleManagement;
